import _extends from "@babel/runtime/helpers/esm/extends";
import { css as _css } from "@emotion/react";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["align", "children", "className", "onComplete", "onReverseComplete", "open", "setIsOpen", "panelWidth"];
import * as React from 'react';
import clsx from 'clsx';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';
import useClickAway from 'react-use/lib/useClickAway';
import { useTheme } from '@emotion/react';
import { motion, AnimatePresence } from 'framer-motion';
import Portal from '@reach/portal';
import { misc } from '../assets/styles/utility';
import Close from './Close';
import { jsx as ___EmotionJSX } from "@emotion/react";
export const StylesOffCanvasVariables = theme => ({
  padding: theme.space.large,
  panelOffset: theme.space.large
});

const OffCanvas = _ref => {
  let {
    align = 'left',
    children,
    className,
    onComplete = () => {},
    onReverseComplete = () => {},
    open,
    setIsOpen,
    panelWidth = 300
  } = _ref,
      rest = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const offCanvasRef = React.useRef();
  const offCanvasPanelRef = React.useRef();
  const direction = React.useRef('forward');

  const handleOnComplete = () => {
    offCanvasPanelRef.current.focus();
    onComplete();
  };

  const offCanvasVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1
    }
  };
  const offCanvasDialogVariants = {
    hidden: {
      opacity: 0,
      x: align === 'left' ? '-100%' : '100%'
    },
    visible: {
      opacity: 1,
      x: 0
    }
  }; // On unmount, clear any/all locks on `<body />`

  React.useEffect(() => () => {
    clearAllBodyScrollLocks();
  }, []);
  useUpdateEffect(() => {
    if (open) {
      disableBodyScroll(offCanvasPanelRef.current);
      direction.current = 'forward';
    } else {
      enableBodyScroll(offCanvasPanelRef.current);
      direction.current = 'reverse';
    }
  }, [open]);
  useClickAway(offCanvasPanelRef, () => setIsOpen(false));
  return ___EmotionJSX(Portal, null, ___EmotionJSX(AnimatePresence, {
    onExitComplete: onReverseComplete
  }, open && ___EmotionJSX(motion.div, _extends({
    css: /*#__PURE__*/_css({
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      background: theme.color.dark.overlay,
      zIndex: 10
    }, process.env.NODE_ENV === "production" ? "" : ";label:OffCanvas;"),
    className: clsx('CK__OffCanvas', className),
    ref: offCanvasRef,
    variants: offCanvasVariants,
    initial: "hidden",
    animate: "visible",
    exit: "hidden",
    onAnimationComplete: () => direction.current === 'forward' && handleOnComplete()
  }, rest), ___EmotionJSX(motion.aside, {
    css: [misc.overflow, {
      position: 'absolute',
      top: 0,
      [align]: 0,
      zIndex: 5,
      height: '100%',
      width: `calc(100% - ${theme.space.base}px)`,
      background: theme.color.light.base,
      padding: StylesOffCanvasVariables(theme).padding,
      boxShadow: theme.boxShadow.large,
      [theme.mq.small]: {
        width: panelWidth
      }
    }, align === 'left' && {
      borderTopRightRadius: theme.borderRadius.xlarge
    }, align === 'right' && {
      borderTopLeftRadius: theme.borderRadius.xlarge
    }, process.env.NODE_ENV === "production" ? "" : ";label:OffCanvas;"],
    className: `CK__OffCanvas__Panel ${theme.settings.classes.trim}`,
    ref: offCanvasPanelRef,
    variants: offCanvasDialogVariants,
    initial: "hidden",
    animate: "visible",
    exit: "hidden",
    transition: {
      x: {
        stiffness: 1000
      }
    }
  }, ___EmotionJSX(Close, {
    onClick: () => setIsOpen(false),
    css: /*#__PURE__*/_css({
      position: 'absolute',
      top: StylesOffCanvasVariables(theme).padding,
      right: StylesOffCanvasVariables(theme).padding,
      zIndex: 10
    }, process.env.NODE_ENV === "production" ? "" : ";label:OffCanvas;"),
    className: "CK__OffCanvas__Close"
  }), children))));
};

export default OffCanvas;